import { useTranslation } from "react-i18next";
import type { StyleSpecification } from "maplibre-gl";
import { getMainColor, MainColor, setMainColor } from "../../libs/simple/main";
import {
  canSplit,
  differsByZoom,
  getZoomColors,
  isSplit,
  joinZoom,
  setZoomColor,
  splitByZoom,
  ZoomSide,
  zoomRange,
} from "../../libs/simple/zoom";
import SimpleSwatch from "./SimpleSwatch";
import type { PreviewSession } from "./usePreviewSession";

type MainSwatchProps = {
  main: MainColor;
  mapStyle: StyleSpecification;
  session: PreviewSession;
  // Offer a far and a close colour. Only in a component's own view, so the
  // overview stays one colour per row.
  zoomControls?: boolean;
  // The map's current zoom, to show where it sits between far and close.
  zoom?: number;
  // Whether the user switched zoom colours on for this family during this
  // visit. Kept by the panel, so it survives leaving this view. A family
  // split earlier whose two ends are still the same reads as one colour,
  // which is what it looks like on the map.
  zoomOpened?: boolean;
  onZoomOpened?(opened: boolean): void;
  // Pointing at the colour lights up where the family is used on the map.
  onPoint?(pointing: boolean): void;
};

const clamp = (value: number) => Math.min(1, Math.max(0, value));

// One named colour that moves a whole family of shades with it.
export default function MainSwatch(props: MainSwatchProps) {
  const { t } = useTranslation();
  const { main, session, mapStyle } = props;
  const opened = !!props.zoomOpened;
  const setOpened = (value: boolean) => props.onZoomOpened?.(value);

  const color = getMainColor(mapStyle, main);
  if (!color) return null;

  const title = t(main.title);
  const zoomColors = getZoomColors(mapStyle, main);
  const zoomOn =
    !!props.zoomControls && !!zoomColors && (opened || differsByZoom(mapStyle, main));
  const offerZoom = !!props.zoomControls && (isSplit(mapStyle, main) || canSplit(mapStyle, main));

  const toggle = offerZoom && (
    <label className="studio-zoom-toggle">
      <input
        type="checkbox"
        role="switch"
        checked={zoomOn}
        data-wd-key={`simple:zoom:${main.id}`}
        onChange={(e) => {
          if (e.target.checked) {
            setOpened(true);
            session.apply((current) => splitByZoom(current, main));
          } else {
            setOpened(false);
            session.apply((current) => joinZoom(current, main));
          }
        }}
      />
      <span>{t("Changes with zoom")}</span>
    </label>
  );

  if (!zoomOn || !zoomColors) {
    return (
      <>
        <SimpleSwatch
          slotKey={`main.${main.id}`}
          label={title}
          color={color}
          onPreview={(next) =>
            session.preview((base) => setMainColor(base, main, next))
          }
          onCommitPreview={session.commit}
          onCancelPreview={session.cancel}
          onPoint={props.onPoint}
          // Main colours set hue, saturation and lightness; every place in
          // the family keeps its own transparency.
          solid
          onApply={(next) =>
            session.apply((current) => setMainColor(current, main, next))
          }
        />
        {toggle}
      </>
    );
  }

  const range = zoomRange(mapStyle, main);
  const position =
    range && props.zoom !== undefined
      ? clamp((props.zoom - range.out) / (range.in - range.out))
      : undefined;

  const side = (end: ZoomSide, label: string) => (
    <SimpleSwatch
      slotKey={`main.${main.id}.${end}`}
      label={label}
      color={zoomColors[end]}
      onPreview={(next) =>
        session.preview((base) => setZoomColor(base, main, end, next))
      }
      onCommitPreview={session.commit}
      onCancelPreview={session.cancel}
      onPoint={props.onPoint}
      solid
      onApply={(next) =>
        session.apply((current) => setZoomColor(current, main, end, next))
      }
    />
  );

  return (
    <div className="studio-zoom" data-wd-key={`simple:zoom:${main.id}:group`}>
      <div className="studio-zoom__title">{title}</div>
      {side("out", t("Zoomed out"))}
      {side("in", t("Zoomed in"))}
      <div
        className="studio-zoom__meter"
        data-wd-key={`simple:zoom:${main.id}:meter`}
        title={
          props.zoom === undefined
            ? undefined
            : t("The map is at zoom {{zoom}}", { zoom: props.zoom.toFixed(1) })
        }
      >
        <div
          className="studio-zoom__track"
          style={{
            background: `linear-gradient(to right, ${zoomColors.out}, ${zoomColors.in})`,
          }}
        >
          {position !== undefined && (
            <span
              className="studio-zoom__now"
              style={{ insetInlineStart: `${position * 100}%` }}
            />
          )}
        </div>
        <div className="studio-zoom__ends" aria-hidden="true">
          <span>{t("Far")}</span>
          <span>{t("Close")}</span>
        </div>
      </div>
      {toggle}
    </div>
  );
}
